import React, { useState, useRef, useEffect, useCallback } from "react";
import { Send, Loader2, User, ChevronDown, ChevronUp, MessageSquare, ArrowDown, Mic } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { StickToBottom, useStickToBottomContext } from "use-stick-to-bottom";
import { RimaLogo } from "./RimaLogo";
import { useSpeechToText } from "../hooks/useSpeechToText";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  isTyping?: boolean;
}

interface ChatPanelProps {
  runId?: string;
}

const API_BASE_URL = process.env.API_BASE_URL || "https://api-test.getrima.ai";

// Typing message component with character-by-character reveal
function TypingMessage({ content, onComplete }: { content: string; onComplete?: () => void }) {
  const [typedContent, setTypedContent] = useState("");
  const typingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Cleanup function
    const cleanup = () => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
      }
    };

    cleanup();

    if (content.length === 0) {
      setTypedContent("");
      return cleanup;
    }

    const step = 4; // Characters per interval
    const delay = 6; // ms

    const initialLength = Math.min(step, content.length);
    setTypedContent(content.slice(0, initialLength));

    let index = initialLength;
    if (index >= content.length) {
      onComplete?.();
      return cleanup;
    }

    typingIntervalRef.current = setInterval(() => {
      index = Math.min(index + step, content.length);
      setTypedContent(content.slice(0, index));

      if (index >= content.length) {
        if (typingIntervalRef.current) {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
        }
        onComplete?.();
      }
    }, delay);

    return cleanup;
  }, [content, onComplete]);

  return (
    <div className="text-sm leading-relaxed prose prose-sm max-w-none prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-code:text-xs prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-gray-900 prose-pre:text-gray-100">
        <ReactMarkdown>{typedContent}</ReactMarkdown>
      </div>
  );
}

// Scroll to bottom button
function ScrollToBottomButton() {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  const handleClick = useCallback(() => {
    scrollToBottom();
  }, [scrollToBottom]);

  if (isAtBottom) return null;

  return (
    <button
      onClick={handleClick}
      className="absolute bottom-2 left-1/2 -translate-x-1/2 p-1.5 bg-white border border-gray-200 rounded-full shadow-md hover:bg-gray-50 transition-colors z-10"
    >
      <ArrowDown className="w-3.5 h-3.5 text-gray-600" />
    </button>
  );
}

export function ChatPanel({ runId }: ChatPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [typingMessageId, setTypingMessageId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Voice input refs
  const baseTextRef = useRef<string>("");
  const finalTranscriptRef = useRef<string>("");
  const lastPartialRef = useRef<string>("");
  const partialTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (partialTimerRef.current) {
        clearTimeout(partialTimerRef.current);
      }
    };
  }, []);

  const appendWithSpace = (base: string, addition: string) => {
    const trimmed = addition.trim();
    if (!trimmed) return base;
    if (!base) return trimmed;
    const needsSpace = !/\s$/.test(base);
    return `${base}${needsSpace ? " " : ""}${trimmed}`;
  };

  const buildVoiceText = (partial?: string) => {
    let combined = baseTextRef.current;
    combined = appendWithSpace(combined, finalTranscriptRef.current);
    if (partial) {
      combined = appendWithSpace(combined, partial);
    }
    return combined;
  };

  const { isRecording, toggleRecording: originalToggle } = useSpeechToText({
    onTranscript: (text) => {
      lastPartialRef.current = text;
      if (partialTimerRef.current) return;
      partialTimerRef.current = setTimeout(() => {
        partialTimerRef.current = null;
        if (lastPartialRef.current) {
          setInputValue(buildVoiceText(lastPartialRef.current));
        }
      }, 150);
    },
    onFinalTranscript: (text) => {
      if (partialTimerRef.current) {
        clearTimeout(partialTimerRef.current);
        partialTimerRef.current = null;
      }
      lastPartialRef.current = "";
      finalTranscriptRef.current = appendWithSpace(finalTranscriptRef.current, text);
      setInputValue(buildVoiceText());
    },
    onError: (error) => {
      console.error("Voice input error:", error);
    },
  });

  const toggleVoice = () => {
    if (!isRecording) {
      // Starting recording - capture current text as base
      baseTextRef.current = inputValue;
      finalTranscriptRef.current = "";
      lastPartialRef.current = "";
    }
    originalToggle();
  };

  // Clear messages when run changes
  useEffect(() => {
    setMessages([]);
    setInputValue("");
    setTypingMessageId(null);
  }, [runId]);

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isExpanded]);

  const handleTypingComplete = useCallback((messageId: string) => {
    setTypingMessageId((prev) => (prev === messageId ? null : prev));
  }, []);

  const handleSend = async () => {
    if (!inputValue.trim() || isLoading || !runId) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: inputValue.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue("");
    setIsLoading(true);

    try {
      // Build conversation history
      const conversationHistory = [...messages, userMessage].map((msg) => ({
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp.toISOString(),
      }));

      const token = localStorage.getItem("rima_token");
      const response = await fetch(`${API_BASE_URL}/api/v1/recon/runs/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          run_id: runId,
          message: userMessage.content,
          conversation_history: conversationHistory,
        }),
      });

      const data = await response.json();

      if (data.success && data.answer) {
        const assistantMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: data.answer,
          timestamp: new Date(),
          isTyping: true,
        };
        setMessages((prev) => [...prev, assistantMessage]);
        setTypingMessageId(assistantMessage.id);
      } else if (data.error) {
        const errorMessage: Message = {
          id: (Date.now() + 1).toString(),
          role: "assistant",
          content: `Sorry, I encountered an error: ${data.error}`,
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      }
    } catch (error) {
      console.error("Chat error:", error);
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "Sorry, I couldn't connect to the server. Please try again.",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="border-t border-gray-100 bg-white">
      {/* Toggle Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-2.5 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Chat</span>
          {messages.length > 0 && (
            <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
              {messages.length}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronDown className="w-4 h-4 text-gray-400" />
        ) : (
          <ChevronUp className="w-4 h-4 text-gray-400" />
        )}
      </button>

      {/* Chat Content (Collapsible) */}
      {isExpanded && (
        <div className="border-t border-gray-100 flex flex-col" style={{ height: "280px" }}>
          {/* Messages with StickToBottom */}
          <StickToBottom
            className="flex-1 overflow-y-auto relative"
            initial="smooth"
            resize="smooth"
          >
            <StickToBottom.Content className="p-3 space-y-3 bg-gray-50/50">
              {messages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-center py-8">
                  <RimaLogo size={32} />
                  <p className="text-xs text-gray-400 mt-2">
                    {runId
                      ? "Ask me anything about this reconciliation"
                      : "Select a cell to start chatting"}
                  </p>
                </div>
              )}

              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex gap-2 ${
                    message.role === "user" ? "flex-row-reverse" : ""
                  }`}
                >
                  <div
                    className={`flex-shrink-0 ${
                      message.role === "user"
                        ? "w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center"
                        : ""
                    }`}
                  >
                    {message.role === "user" ? (
                      <User className="w-3.5 h-3.5 text-gray-600" />
                    ) : (
                      <RimaLogo size={24} />
                    )}
                  </div>
                  <div
                    className={`max-w-[85%] rounded-lg px-3 py-2 ${
                      message.role === "user"
                        ? "bg-gray-900 text-white"
                        : "bg-white border border-gray-100 text-gray-700"
                    }`}
                  >
                    {message.role === "assistant" ? (
                      typingMessageId === message.id ? (
                        <TypingMessage
                          content={message.content}
                          onComplete={() => handleTypingComplete(message.id)}
                        />
                      ) : (
                        <div className="text-sm leading-relaxed prose prose-sm max-w-none prose-p:my-1 prose-headings:my-2 prose-ul:my-1 prose-ol:my-1 prose-li:my-0.5 prose-code:text-xs prose-code:bg-gray-100 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-pre:bg-gray-900 prose-pre:text-gray-100">
                          <ReactMarkdown>{message.content}</ReactMarkdown>
                        </div>
                      )
                    ) : (
                      <p className="text-sm whitespace-pre-wrap leading-relaxed">
                        {message.content}
                      </p>
                    )}
                  </div>
                </div>
              ))}

              {/* Loading indicator */}
              {isLoading && (
                <div className="flex gap-2">
                  <RimaLogo size={24} />
                  <div className="bg-white border border-gray-100 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                      <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                      <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              )}
            </StickToBottom.Content>
            <ScrollToBottomButton />
          </StickToBottom>

          {/* Input */}
          <div className="p-3 bg-white border-t border-gray-100">
            <div className="flex items-center gap-2">
              <button
                onClick={toggleVoice}
                disabled={!runId}
                className={`p-2 rounded-lg transition-colors ${
                  isRecording
                    ? "bg-red-100 text-red-600 hover:bg-red-200"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed"
                }`}
                title={isRecording ? "Stop recording" : "Start voice input"}
              >
                <Mic className={`w-4 h-4 ${isRecording ? "animate-pulse" : ""}`} />
              </button>
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={runId ? "Ask a question..." : "Select a cell first..."}
                className="flex-1 text-sm px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={isLoading || !runId}
              />
              <button
                onClick={handleSend}
                disabled={!inputValue.trim() || isLoading || !runId}
                className="p-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
